const NEIGHBORHOOD = 2;
/**
 * Heuristic triage. This is deliberately boring and explainable: an agent reads
 * the `rootCause` line and decides whether to trust it. A confident-sounding but
 * wrong explanation is worse than a cautious one, so every branch says what it
 * saw rather than guessing at intent.
 */
export function inferFailureKind(step, observations, consoleErrors) {
    // Deliberately matched against the *error*, not the description. "Click the
    // Sign in button" must never be classified as an auth failure just because
    // the words "sign in" appear in it.
    const text = `${step.error ?? ''}`.toLowerCase();
    const described = `${step.description}`.toLowerCase();
    if (step.type === 'dependency')
        return 'dependency';
    if (/timed out|timeout/.test(text))
        return 'timeout';
    if (/unauthorized|forbidden|\b401\b|\b403\b|not authenticated|login required|session (expired|missing)/.test(text)) {
        return 'auth';
    }
    if (/net::|err_|econn|enotfound|certificate|network|failed to fetch|socket hang up/.test(text)) {
        return 'network-error';
    }
    if (/could not act on|no visible element|not visible|waiting for locator|unable to find|no such element/.test(text)) {
        return 'element-missing';
    }
    if (/did not satisfy|expected the page|expected the response/.test(text))
        return 'assertion';
    if (step.type === 'assertion')
        return 'assertion';
    if (step.type === 'navigation')
        return 'navigation';
    if (consoleErrors.length > 0)
        return 'runtime-error';
    if (observations?.pageUrl && step.resolvedTarget && described.includes('http'))
        return 'navigation';
    return 'infrastructure';
}
export function inferRootCause(kind, step, observations, consoleErrors) {
    const where = observations?.pageUrl ? ` (on ${observations.pageUrl})` : '';
    const detail = step.error ? ` Driver reported: ${truncate(step.error, 400)}` : '';
    switch (kind) {
        case 'assertion':
            return `The page${where} did not satisfy "${step.description}"${detail}`;
        case 'element-missing':
            return `The element referenced by "${step.description}" was not found${where}.${detail}`;
        case 'navigation':
            return `Navigation for "${step.description}" did not reach the expected page${where}.${detail}`;
        case 'timeout':
            return `"${step.description}" exceeded its time budget${where}.${detail}`;
        case 'auth':
            return `The environment rejected the session during "${step.description}"${where}.${detail}`;
        case 'network-error':
            return `A network failure interrupted "${step.description}"${where}.${detail}`;
        case 'runtime-error':
            return `The page threw a runtime error while running "${step.description}". ${consoleErrors
                .slice(0, 3)
                .join(' | ')}`;
        case 'dependency':
            return `Step ${step.index + 1} never ran because a step it depends on did not pass.`;
        case 'healed':
            return `The test drifted from the current UI. Verifyloop repaired the plan and the rerun passed.`;
        case 'flaky':
            return `This test produced different results across repeated runs — treat the failure as non-deterministic.`;
        case 'infrastructure':
        default:
            return `The run stopped at "${step.description}"${where}.${detail || 'No page-level error was captured.'}`;
    }
}
export function suggestFix(kind, step, observations) {
    const pageHint = observations?.pageUrl ? ` Current page: ${observations.pageUrl}.` : '';
    switch (kind) {
        case 'assertion':
            return {
                text: `Decide whether the product or the test is wrong. If the UI text changed, update the assertion target (currently ${step?.target ?? 'unset'}). If the product regressed, fix the component that renders it.${pageHint}`,
            };
        case 'element-missing':
            return {
                target: step?.target,
                text: `Either the element is not rendered in this state, or the locator is stale. Check the DOM snapshot for a nearby stable hook (role, label, or a data-testid on the project's testIdAttributes).`,
            };
        case 'navigation':
            return {
                target: step?.target,
                text: `The route did not resolve. Check the path, any auth redirect, and whether the app served the expected status.${pageHint}`,
            };
        case 'timeout':
            return {
                text: `The step waited without the condition becoming true. Look for a pending network request or a spinner in the DOM snapshot before changing the timeout.`,
            };
        case 'auth':
            return {
                text: `Re-authenticate: update the environment credentials (project env update) or check that the session is still valid for this environment.`,
            };
        case 'network-error':
            return {
                text: `This looks like infrastructure, not product code. Re-run once; if it persists, capture the request in the network errors above.`,
            };
        case 'runtime-error':
            return {
                text: `A JavaScript error was thrown in the page. The first console entry above is usually the frame to fix.`,
            };
        case 'healed':
            return { text: `Review the healed plan (see healedAt on the test) and commit it if it matches intent.` };
        case 'flaky':
            return { text: `Look for shared state or ordering dependence between runs before trusting either verdict.` };
        case 'dependency':
        case 'infrastructure':
        default:
            return { text: `Reproduce locally against the same environment, then re-run with --output json for the full bundle.` };
    }
}
/**
 * Assemble the single self-consistent bundle an agent consumes. Every artifact
 * referenced here was captured during this single run under this snapshotId —
 * that invariant is what stops an agent reasoning over mixed-up context.
 */
export function buildFailureBundle(context) {
    const { run, test, failingStepIndex, observations } = context;
    const failingStep = run.steps[failingStepIndex];
    const consoleErrors = context.consoleErrors ?? [];
    const networkErrors = context.networkErrors ?? [];
    const kind = run.failureKind ?? inferFailureKind(failingStep, observations, consoleErrors);
    // A bundle is a claim about one moment, and `test` is mutable: it is edited
    // after a fix, rewritten by the healer, renamed by a person. Anything read
    // from it is only trustworthy while its `codeVersion` still matches the run's.
    // Handing an agent source from a later edit — under a bundle that names the
    // earlier version — is exactly the confident, wrong fix this invariant exists
    // to prevent, so a mismatch omits the source rather than mislabelling it.
    const sameVersion = test.codeVersion === run.codeVersion;
    const planStep = sameVersion ? test.planSteps[failingStepIndex] : undefined;
    const fix = suggestFix(kind, planStep, observations);
    const start = Math.max(0, failingStepIndex - NEIGHBORHOOD);
    const end = Math.min(run.steps.length - 1, failingStepIndex + NEIGHBORHOOD);
    const neighborSteps = run.steps.filter((_, index) => index !== failingStepIndex && index >= start && index <= end);
    return {
        snapshotId: run.id,
        runId: run.id,
        testId: run.testId,
        projectId: run.projectId,
        environmentId: run.environmentId,
        capturedAt: new Date().toISOString(),
        kind,
        failingStepIndex,
        failingStep,
        neighborSteps,
        rootCause: inferRootCause(kind, failingStep, observations, consoleErrors),
        suggestedFix: fix.text,
        suggestedFixTarget: fix.target,
        codeVersion: run.codeVersion,
        ...(sameVersion && test.generatedCode ? { source: test.generatedCode } : {}),
        consoleErrors,
        networkErrors,
    };
}
export function summarizeBundle(bundle) {
    const lines = [
        `${bundle.kind.toUpperCase()} in "${bundle.failingStep.description}"`,
        `  test:    ${bundle.testId}`,
        `  run:     ${bundle.runId}  (snapshot ${bundle.snapshotId})`,
        `  cause:   ${bundle.rootCause}`,
        `  fix:     ${bundle.suggestedFix}`,
    ];
    return lines.join('\n');
}
/**
 * Prompt-engineered evidence envelope for coding agents (Ticket 16).
 * Strictly budget-capped under 1,500 tokens to maximize diagnostic signal
 * without dumping noisy multi-megabyte HTML dumps into LLM context windows.
 */
export function formatEvidenceForAgent(bundle, meta) {
    const lines = [
        '### Justloop Verification Failure',
        `- **Test**: "${meta?.testName ?? bundle.testId}" (\`${bundle.testId}\`)`,
        `- **Verdict**: FAILED (Step ${bundle.failingStepIndex + 1})`,
        `- **Reason**: \`${bundle.kind}\``,
    ];
    if (meta?.pageUrl) {
        lines.push(`- **Target URL**: ${meta.pageUrl}`);
    }
    if (bundle.provenance?.gitCommit) {
        lines.push(`- **Code Provenance**: \`${bundle.provenance.gitCommit.slice(0, 8)}\` (branch: \`${bundle.provenance.gitBranch ?? 'unknown'}\`, dirty: ${bundle.provenance.gitDirty ?? false})`);
    }
    if (bundle.failingStep.resolutionStrategy) {
        lines.push(`- **Locator Strategy**: \`${bundle.failingStep.resolutionStrategy}\`${bundle.failingStep.resolvedTarget ? ` on target \`${bundle.failingStep.resolvedTarget}\`` : ''}`);
    }
    lines.push('', '#### Root Cause', bundle.rootCause);
    if (bundle.suggestedFix) {
        lines.push('', '#### Suggested Fix', bundle.suggestedFix);
        if (bundle.suggestedFixTarget) {
            lines.push(`Target: \`${bundle.suggestedFixTarget}\``);
        }
    }
    if (bundle.consoleErrors.length > 0) {
        lines.push('', '#### Console Errors');
        for (const err of bundle.consoleErrors.slice(0, 5)) {
            lines.push(`- \`${truncate(err, 200)}\``);
        }
    }
    if (bundle.networkErrors.length > 0) {
        lines.push('', '#### Network Errors');
        for (const net of bundle.networkErrors.slice(0, 5)) {
            lines.push(`- \`${truncate(net, 200)}\``);
        }
    }
    if (meta?.artifactUrls && Object.keys(meta.artifactUrls).length > 0) {
        lines.push('', '#### Forensic Artifacts');
        for (const [id, url] of Object.entries(meta.artifactUrls)) {
            lines.push(`- Artifact \`${id}\`: ${url}`);
        }
    }
    return lines.join('\n');
}
function truncate(value, max) {
    return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}
//# sourceMappingURL=bundle.js.map